import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Modal,
  Typography,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import VerifiedIcon from "@mui/icons-material/Verified";
import moment from "moment";
import { BASE_URL, DISABLED_MODULE } from "../../global";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import { parseRange } from "../../utils/components/accuracyandlcandrangesshow";

/* ── Google Fonts: DM Sans + Geist Mono  */
if (!document.getElementById("cdp-fonts")) {
  const link = document.createElement("link");
  link.id = "cdp-fonts";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Geist+Mono:wght@400;500;600&display=swap";
  document.head.appendChild(link);
}

const FONT = "'DM Sans', sans-serif";
const MONO = "'Geist Mono', monospace";

const previewModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: 540 },
  bgcolor: "#ffffff",
  borderRadius: "12px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  overflow: "hidden",
  outline: "none",
  fontFamily: FONT,
};

/* ── Section chip label  */
const SectionLabel = ({ children }) => (
  <Box
    sx={{
    display: "flex",            // change from inline-flex
      justifyContent: "center",   // center horizontally
      alignItems: "center",
      width: "100%", 
      mb: 1.2,
      px: 1,
      py: "5px",
      bgcolor: "#eff6ff",
      borderRadius: "6px",
      border: "1px solid #dbeafe",
    }}
  >
    <Typography
      sx={{
        fontFamily: FONT,
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "0.09em",
        textTransform: "uppercase",
        color: "#1d4ed8",
      }}
    >
      {children}
    </Typography>
  </Box>
);

/* ── Label / Value row  */
const InfoRow = ({ label, value }) => (
  <Grid container sx={{ mb: 0.65, alignItems: "baseline" }}>
    <Grid item xs={5}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: "12px",
          fontWeight: 700,
          color: "#374151",
          letterSpacing: "0.01em",
        }}
      >
        {label}
      </Typography>
    </Grid>
    <Grid item xs={7}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: "12.5px",
          fontWeight: 500,
          color: "#111827",
          wordBreak: "break-word",
          lineHeight: 1.45,
        }}
      >
        : {value || "—"}
      </Typography>
    </Grid>
  </Grid>
);

export default function CertificateDownloadPreview({
  open,
  onClose,
  onDownload,
  clientId,
  clientName,
  address,
  instrumentName,
  ranges,
  lc,
  serialNo,
  make,
  models,
  calibrationDate,
  nextDueDate,
  calibrationFrequency,
  certificateNumber,
  ulrNumber
}) {
  const [step, setStep] = useState("preview");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const resetState = () => {
    setStep("preview");
    setEmail("");
    setOtp("");
    setLoading(false);
    setError("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  /* ── Helpers */
  const formatDate = (d) => d && moment(d).isValid() ? moment(d).format("DD/MM/YYYY") : "—";

  const dueDateDisplay =
    calibrationFrequency === "Not Required"
      ? "Not Required"
      : calibrationFrequency === "Customer Defined"
      ? "Customer Defined"
      : formatDate(nextDueDate);

  /* ── Send OTP  */
  const handleSendOtp = async () => {
    if (!email.trim()) {
      setError("Please enter an email address.");
      return;
    }
    setError("");
    setLoading(true);

    try {
      const res = await axiosWithToken.get(BASE_URL + `clients?_where=(id,eq,${clientId})`
      );

      const clientData = res.data?.[0];
      if (!clientData) {
        setError("Client not found.");
        setLoading(false);
        return;
      }

      let contacts = [];
      try {
        contacts = JSON.parse(clientData.contact || "[]");
      } catch {
        contacts = [];
      }

      const enteredEmail = email.trim().toLowerCase();
      const topLevelEmail = (clientData.email || "").trim().toLowerCase();

      const matchFound =
        contacts.some(
          (c) => (c.emailId || "").trim().toLowerCase() === enteredEmail
        ) || topLevelEmail === enteredEmail;

      if (!matchFound) {
        setError(
          "Email not registered with this client. Please use a registered email address."
        );
        setLoading(false);
        return;
      }

      await axiosWithToken.post(BASE_URL + `email/send-cert-otp`, {
        userEmail: email.trim(),
        userName: clientName,
      });

      setStep("otp");
    } catch (err) {
      console.error("OTP send error:", err);
      setError("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Verify OTP */
  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setError("Please enter the OTP.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await axiosWithToken.post(BASE_URL + `email/verify-cert-otp`,
         {
          userEmail: email.trim(),
          otp: otp.trim(),
        }
      );

      if (!data?.verification) {
        setError(data?.error || "Invalid OTP. Please check and try again.");
        return;
      }

      setStep("verified");
    } catch (err) {
      console.error("Verification error:", err);
      const serverMsg = err?.response?.data?.error;
      // If too many attempts, go back to email step so user can re-request
      if (err?.response?.status === 429) {
        setError(serverMsg || "Too many attempts. Please request a new OTP.");
        setOtp("");
      } else {
        setError(serverMsg || "Invalid OTP. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Resend OTP  */
  const handleResendOtp = async () => {
    setOtp("");
    setError("");
    setLoading(true);
    try {
      await axiosWithToken.post(BASE_URL + `email/send-cert-otp`, {
        userEmail: email.trim(),
        userName: clientName,
      });
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Header copy */
  const headerTitle = {
    preview: "Certificate Preview",
    email: "Email Verification",
    otp: "Enter OTP",
    verified: "Verified",
  }[step];

  const headerSubtitle = {
    preview: "Review details before downloading",
    email: "Enter your registered email to receive OTP",
    otp: `OTP sent to ${email}`,
    verified: "Identity verified — you may download now",
  }[step];

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={previewModalStyle}>
        {/* ── Header ── */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1e40af 0%, #1d4ed8 50%, #2563eb 100%)",
            px: 3,
            py: 2.2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "1px",
              background: "rgba(255,255,255,0.15)",
            },
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "15px",
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}
            >
              {headerTitle}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "11.5px",
                fontWeight: 400,
                color: "#bfdbfe",
                mt: 0.3,
              }}
            >
              {headerSubtitle}
            </Typography>
          </Box>
          <Button
            onClick={handleClose}
            size="small"
            sx={{
              minWidth: 0,
              color: "rgba(255,255,255,0.75)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)", color: "#fff" },
              borderRadius: "8px",
              p: "5px",
              transition: "all 0.15s",
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>

        {/* ── Body ── */}
        <Box sx={{ px: 3, py: 2.3, maxHeight: "65vh", overflowY: "auto" }}>
          {/* ── STEP: preview ── */}
          {step === "preview" && (
            <>
              <SectionLabel>Customer Details</SectionLabel>
              <InfoRow label="Customer Name" value={clientName} />
              <InfoRow label="Address" value={address} />

              <SectionLabel>Instrument Details</SectionLabel>
              <InfoRow label="Instrument Name" value={instrumentName} />
              <InfoRow label="Make" value={make} />
              <InfoRow label="Model" value={models} />
              <InfoRow label="Serial Number" value={serialNo} />
              <InfoRow label="Range" value={ranges} />
              <InfoRow label="Least Count" value={parseRange(lc)} />

              <SectionLabel>Calibration Details</SectionLabel>
              <InfoRow label="Calibration Date" value={formatDate(calibrationDate)} />
              <InfoRow label="Due Date" value={dueDateDisplay} />
              <InfoRow label="Certificate No" value={certificateNumber} />
              {ulrNumber && (<InfoRow label="ULR Number" value={ulrNumber} />)} 
            </>
          )}

          {/* ── STEP: email ── */}
          {step === "email" && (
            <Box sx={{ mt: 0.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 2.5,
                  p: 1.8,
                  bgcolor: "#eff6ff",
                  borderRadius: "10px",
                  border: "1px solid #bfdbfe",
                }}
              >
                <EmailIcon sx={{ color: "#2563eb", fontSize: 18, mt: 0.15, flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontFamily: FONT, fontSize: "12px", fontWeight: 400, color: "#1e40af", lineHeight: 1.55,
                  }}
                >
                  Enter the email address registered with this client account.
                  An OTP will be sent for verification.
                </Typography>
              </Box>
              <TextField
                label="Email Address"
                type="email"
                size="small"
                fullWidth
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                placeholder="e.g. yourname@company.com"
                autoFocus
                InputProps={{ style: { fontFamily: FONT, fontSize: "13px" } }}
                InputLabelProps={{ style: { fontFamily: FONT, fontSize: "13px" } }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2563eb" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2563eb", borderWidth: "2px" },
                  },
                }}
              />
              {error && (
                <Alert
                  severity="error"
                  sx={{ mt: 1.5, py: 0.5, fontSize: "12px", fontFamily: FONT, borderRadius: "8px" }}
                >
                  {error}
                </Alert>
              )}
            </Box>
          )}

          {/* ── STEP: otp ── */}
          {step === "otp" && (
            <Box sx={{ mt: 0.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 2.5,
                  p: 1.8,
                  bgcolor: "#f0fdf4",
                  borderRadius: "10px",
                  border: "1px solid #bbf7d0",
                }}
              >
                <EmailIcon sx={{ color: "#16a34a", fontSize: 18, mt: 0.15, flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "#166534",
                    lineHeight: 1.55,
                  }}
                >
                  OTP sent to{" "}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {email}
                  </Box>
                  . Please check your inbox and enter the 6-digit code below.
                </Typography>
              </Box>
              <TextField
                label="Enter OTP"
                size="small"
                fullWidth
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                placeholder="• • • • • •"
                inputProps={{
                  maxLength: 6,
                  style: {
                    fontFamily: MONO,
                    letterSpacing: "10px",
                    fontWeight: 600,
                    fontSize: "20px",
                    textAlign: "center",
                  },
                }}
                InputLabelProps={{ style: { fontFamily: FONT, fontSize: "13px" } }}
                autoFocus
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#16a34a" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#16a34a", borderWidth: "2px" },
                  },
                }}
              />
              {error && (
                <Alert
                  severity="error"
                  sx={{ mt: 1.5, py: 0.5, fontSize: "12px", fontFamily: FONT, borderRadius: "8px" }}
                >
                  {error}
                </Alert>
              )}
              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Button
                  size="small"
                  onClick={() => {
                    setStep("email");
                    setOtp("");
                    setError("");
                  }}
                  sx={{
                    fontFamily: FONT,
                    fontSize: "11.5px",
                    fontWeight: 500,
                    color: "#64748b",
                    textTransform: "none",
                    "&:hover": { color: "#334155", bgcolor: "#f8fafc" },
                    borderRadius: "6px",
                    px: 1,
                  }}
                >
                  ← Change Email
                </Button>
                <Button
                  size="small"
                  onClick={handleResendOtp}
                  disabled={loading}
                  sx={{
                    fontFamily: FONT,
                    fontSize: "11.5px",
                    fontWeight: 600,
                    color: "#2563eb",
                    textTransform: "none",
                    "&:hover": { bgcolor: "#eff6ff" },
                    borderRadius: "6px",
                    px: 1,
                  }}
                >
                  {loading ? "Sending…" : "Resend OTP"}
                </Button>
              </Box>
            </Box>
          )}

          {/* ── STEP: verified ── */}
          {step === "verified" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 3.5,
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "#f0fdf4",
                  border: "2px solid #bbf7d0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <VerifiedIcon sx={{ color: "#16a34a", fontSize: 40 }} />
              </Box>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#166534",
                  letterSpacing: "-0.01em",
                }}
              >
                Email Verified Successfully!
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: "12.5px",
                  fontWeight: 400,
                  color: "#64748b",
                  textAlign: "center",
                  lineHeight: 1.6,
                  maxWidth: 320,
                }}
              >
                Your identity has been confirmed. Click{" "}
                <Box component="span" sx={{ fontWeight: 600, color: "#166534" }}>
                  Download PDF
                </Box>{" "}
                to get your certificate.
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Footer ── */}
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#f8fafc",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            gap: 1.5,
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleClose}
            sx={{
              fontFamily: FONT,
              fontSize: "12.5px",
              fontWeight: 500,
              borderColor: "#cbd5e1",
              color: "#64748b",
              borderRadius: "8px",
              textTransform: "none",
              px: 2,
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
            }}
          >
            Cancel
          </Button>

          {/* Preview → email */}
          {step === "preview" && (
            <Button
              variant="contained"
              size="small"
              startIcon={<EmailIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => {
                if (DISABLED_MODULE.includes("QREmailVerificationStop")) {
                  onDownload();
                  handleClose();
                } else {
                  setStep("email");
                }
              }}
              sx={{
                fontFamily: FONT,
                fontSize: "12.5px",
                fontWeight: 600,
                background: "linear-gradient(135deg, #1e40af 0%, #2563eb 100%)",
                borderRadius: "8px",
                textTransform: "none",
                px: 2.5,
                boxShadow: "0 2px 8px rgba(37,99,235,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)",
                  boxShadow: "0 4px 12px rgba(37,99,235,0.4)",
                },
              }}
            >
              Request to Download
            </Button>
          )}

          {/* Email → Send OTP */}
          {step === "email" && (
            <Button
              variant="contained"
              size="small"
              onClick={handleSendOtp}
              disabled={loading || !email.trim()}
              startIcon={
                loading
                  ? <CircularProgress size={13} color="inherit" />
                  : <EmailIcon sx={{ fontSize: "15px !important" }} />
              }
              sx={{
                fontFamily: FONT,
                fontSize: "12.5px",
                fontWeight: 600,
                background: "linear-gradient(135deg, #1e40af 0%, #2563eb 100%)",
                borderRadius: "8px",
                textTransform: "none",
                px: 2.5,
                boxShadow: "0 2px 8px rgba(37,99,235,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)",
                },
                "&.Mui-disabled": { opacity: 0.6 },
              }}
            >
              {loading ? "Sending…" : "Send OTP"}
            </Button>
          )}

          {/* OTP → Verify */}
          {step === "otp" && (
            <Button
              variant="contained"
              size="small"
              onClick={handleVerifyOtp}
              disabled={otp.length < 6 || loading}
              startIcon={
                loading ? <CircularProgress size={13} color="inherit" /> : null
              }
              sx={{
                fontFamily: FONT,
                fontSize: "12.5px",
                fontWeight: 600,
                background: "linear-gradient(135deg, #1e40af 0%, #2563eb 100%)",
                borderRadius: "8px",
                textTransform: "none",
                px: 2.5,
                boxShadow: "0 2px 8px rgba(37,99,235,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)",
                },
                "&.Mui-disabled": { opacity: 0.6 },
              }}
            >
              {loading ? "Verifying…" : "Verify OTP"}
            </Button>
          )}

          {/* Verified → Download */}
          {step === "verified" && (
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => {
                onDownload();
                handleClose();
              }}
              sx={{
                fontFamily: FONT,
                fontSize: "12.5px",
                fontWeight: 600,
                background: "linear-gradient(135deg, #15803d 0%, #16a34a 100%)",
                borderRadius: "8px",
                textTransform: "none",
                px: 2.5,
                boxShadow: "0 2px 8px rgba(22,163,74,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #166534 0%, #15803d 100%)",
                  boxShadow: "0 4px 12px rgba(22,163,74,0.4)",
                },
              }}
            >
              Download PDF
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
